import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  CircularProgress,
  Button,
  Autocomplete,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import axios from '../../services/axiosConfig';
import { environment } from '../../config/environment';
import VisitDashboard from './VisitDashboard';

interface ISiteVisit {
  siteId: string;
  type: number;
  user: string;
  customerNo: string;
  reportedOn: string;
}

interface ISiteVisitFilterData {
  siteIds: string[];
  users: string[];
  accountNumbers: string[];
}

const SiteVisit: React.FC = () => {
  const [visits, setVisits] = useState<ISiteVisit[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterData, setFilterData] = useState<ISiteVisitFilterData | null>(null);
  const [siteId, setSiteId] = useState('');
  const [type, setType] = useState('');
  const [username, setUsername] = useState('');
  const [accountNo, setAccountNo] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [startTime, setStartTime] = useState('00:00');
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [endTime, setEndTime] = useState('23:59');
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number | null>(null);

  const combineDateAndTime = (date: string, time: string): string => {
    return `${date}T${time}:00`;
  };

  const loadFilterData = useCallback(async () => {
    try {
      const response = await axios.get(`${environment.apiUrl}visit/filterdata`);
      setFilterData(response.data);
    } catch (error) {
      console.error('Error loading filter data:', error);
    }
  }, []);

  const loadVisits = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (siteId) params.siteId = siteId;
      if (type) params.type = type;
      if (username) params.username = username;
      if (accountNo) params.accountNo = accountNo;
      if (startDate && startTime) params.startDate = combineDateAndTime(startDate, startTime);
      if (endDate && endTime) params.endDate = combineDateAndTime(endDate, endTime);

      const response = await axios.get(`${environment.apiUrl}visit`, { params });
      console.log('Site Visits response:', response.data);

      const visitsData = Array.isArray(response.data) ? response.data : [];
      setVisits(visitsData);
    } catch (error) {
      console.error('Error loading visits:', error);
      setVisits([]);
    } finally {
      setLoading(false);
    }
  }, [siteId, type, username, accountNo, startDate, startTime, endDate, endTime]);

  useEffect(() => {
    loadFilterData();
  }, [loadFilterData]);

  useEffect(() => {
    if (filterData) {
      loadVisits();
    }
  }, [filterData, loadVisits]);

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefreshInterval === null) {
      return; // Auto-refresh disabled
    }

    const intervalId = setInterval(() => {
      loadVisits(); // Reload visits at the specified interval
    }, autoRefreshInterval * 1000); // Convert seconds to milliseconds

    return () => {
      clearInterval(intervalId);
    };
  }, [autoRefreshInterval, loadVisits]);

  const handleSearch = () => {
    setPage(0); // Reset to first page when searching
    loadVisits();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100vw', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, pb: 0 }}>
        <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.secondary' }} gutterBottom>
          Site Visits
        </Typography>

        <Paper sx={{ p: 3, mb: 0, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Autocomplete
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
              size="small"
              options={filterData?.siteIds || []}
              value={siteId || null}
              onChange={(event, newValue) => {
                setSiteId(newValue || '');
              }}
              renderInput={(params) => (
                <TextField {...params} label="Site ID" />
              )}
            />
            <Autocomplete
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
              size="small"
              options={[
                { label: 'Entrance', value: '1' },
                { label: 'Exit', value: '2' }
              ]}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
              value={type ? { label: type === '1' ? 'Entrance' : 'Exit', value: type } : null}
              onChange={(event, newValue) => {
                setType(newValue ? (newValue as { label: string; value: string }).value : '');
              }}
              renderInput={(params) => (
                <TextField {...params} label="Type" />
              )}
              isOptionEqualToValue={(option, value) =>
                (option as { label: string; value: string }).value === (value as { label: string; value: string }).value
              }
            />
            <Autocomplete
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
              size="small"
              options={filterData?.users || []}
              value={username || null}
              onChange={(event, newValue) => {
                setUsername(newValue || '');
              }}
              renderInput={(params) => (
                <TextField {...params} label="User" />
              )}
            />
            <Autocomplete
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
              size="small"
              options={filterData?.accountNumbers || []}
              value={accountNo || null}
              onChange={(event, newValue) => {
                setAccountNo(newValue || '');
              }}
              renderInput={(params) => (
                <TextField {...params} label="Customer No" />
              )}
            />
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
              label="Start Time"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
              label="End Time"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={handleSearch}
              sx={{ flex: 1, minWidth: '120px', height: '40px', maxWidth: 'calc(100% - 200px)' }}
            >
              Search
            </Button>
            <FormControl size="small" sx={{ minWidth: 180, flexShrink: 0 }}>
              <InputLabel id="auto-refresh-label">Auto Refresh</InputLabel>
              <Select
                labelId="auto-refresh-label"
                id="auto-refresh-select"
                value={autoRefreshInterval === null ? '' : String(autoRefreshInterval)}
                label="Auto Refresh"
                onChange={(e) => {
                  const value = e.target.value as string;
                  if (value === '') {
                    setAutoRefreshInterval(null);
                  } else {
                    setAutoRefreshInterval(Number(value));
                  }
                }}
              >
                <MenuItem value="">Off</MenuItem>
                <MenuItem value="15">Every 15 seconds</MenuItem>
                <MenuItem value="60">Every 1 minute</MenuItem>
                <MenuItem value="300">Every 5 minutes</MenuItem>
                <MenuItem value="600">Every 10 minutes</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>
      </Box>

      {/* Tabs for Dashboard and Table views */}
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 0.5, pb: 2 }}>
        <Paper sx={{ borderRadius: 2 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Table View" />
            <Tab label="Dashboard" />
          </Tabs>
        </Paper>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={6} sx={{ flex: 1 }}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, width: '100%', overflowX: 'hidden' }}>
          {tabValue === 0 && (
            <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              {/* Mobile Card View */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                {visits.length === 0 ? (
                  <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      No visits found for the selected filters.
                    </Typography>
                  </Paper>
                ) : (
                  <>
                    {visits.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((visit, index) => (
                    <Card key={index} sx={{ mb: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Site ID:</Typography>
                            <Typography variant="body2" fontWeight={600}>{visit.siteId}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Type:</Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {visit.type === 1 ? 'Entrance' : visit.type === 2 ? 'Exit' : visit.type}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">User:</Typography>
                            <Typography variant="body2" fontWeight={500} sx={{ textAlign: 'right', maxWidth: '60%' }}>
                              {visit.user}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Customer No:</Typography>
                            <Typography variant="body2" fontWeight={500}>{visit.customerNo}</Typography>
                          </Box>
                          <Divider sx={{ my: 0.5 }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Reported On:</Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {new Date(visit.reportedOn).toLocaleString()}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                    ))}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <TablePagination
                        component="div"
                        count={visits.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                      />
                    </Box>
                  </>
                )}
              </Box>

              {/* Desktop Table View */}
              <Paper sx={{ borderRadius: 2, overflow: 'hidden', display: { xs: 'none', md: 'block' } }}>
                {visits.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      No visits found for the selected filters.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table sx={{ minWidth: 650, tableLayout: 'auto' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Site ID</TableCell>
                            <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>User</TableCell>
                            <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Customer No</TableCell>
                            <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Reported On</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {visits.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((visit, index) => (
                            <TableRow
                              key={index}
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                },
                              }}
                            >
                              <TableCell sx={{ wordBreak: 'break-word' }}>{visit.siteId}</TableCell>
                              <TableCell>{visit.type === 1 ? 'Entrance' : visit.type === 2 ? 'Exit' : visit.type}</TableCell>
                              <TableCell sx={{ wordBreak: 'break-word' }}>{visit.user}</TableCell>
                              <TableCell sx={{ wordBreak: 'break-word' }}>{visit.customerNo}</TableCell>
                              <TableCell>
                                {new Date(visit.reportedOn).toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      component="div"
                      count={visits.length}
                      page={page}
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                  </>
                )}
              </Paper>
            </Box>
          )}
          {tabValue === 1 && (
            <VisitDashboard visits={visits} />
          )}
        </Box>
      )}
    </Box>
  );
};

export default SiteVisit;
